import { API } from "@/lib/api";
import { CALENDLY_URL } from "@/data/constants";
import { INDUSTRIES } from "@/data/content";

// Anonymous first-party analytics: no cookies, no PII, tab-scoped random id only.
const SESSION_KEY = "fbd_anon_session";
const LAST_INDUSTRY_KEY = "fbd_last_industry";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLIENT_REPEAT_MS = 1500;
const SLUGS = new Set(INDUSTRIES.map((i) => i.slug));
const recent = new Map();

const CTA_LOCATIONS = {
  "nav-book-cta": "nav",
  "hero-book-cta": "hero",
  "sticky-demo-cta": "sticky_button",
  "invitation-strategy-call-cta": "strategy_invitation",
  "demo-strategy-call-cta": "demo_section",
  "about-cta-book": "about",
  "final-cta-schedule": "final_cta",
  "industry-cta-book": "industry_page_hero",
  "industry-cta-bottom": "industry_page_bottom",
};

const optedOut = () =>
  navigator.globalPrivacyControl === true || navigator.doNotTrack === "1" || window.doNotTrack === "1";

const isAdminPath = () => window.location.pathname.startsWith("/admin");

const uid = () =>
  (window.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

function store(kind) {
  try { return window[kind]; } catch { return null; }
}

function anonSession() {
  const ss = store("sessionStorage");
  let id = ss?.getItem(SESSION_KEY);
  if (!id) { id = uid(); ss?.setItem(SESSION_KEY, id); }
  return id;
}

function utms() {
  const ls = store("localStorage");
  const read = (k) => { try { return JSON.parse(ls?.getItem(k) || "null") || {}; } catch { return {}; } };
  const latest = read("fbd_attr_latest");
  const first = read("fbd_attr_first");
  return Object.fromEntries(UTM_KEYS.map((k) => [k, latest[k] || first[k] || null]).filter(([, v]) => v));
}

export const isIndustry = (slug) => SLUGS.has(slug);

export function rememberIndustry(slug) {
  if (isIndustry(slug)) store("sessionStorage")?.setItem(LAST_INDUSTRY_KEY, slug);
}

const lastIndustry = () => {
  const v = store("sessionStorage")?.getItem(LAST_INDUSTRY_KEY);
  return isIndustry(v) ? v : null;
};

export function track(eventType, { industry = null, service = null, ctaLocation = null } = {}) {
  if (optedOut() || isAdminPath()) return;
  const key = [eventType, industry, service, ctaLocation].join("|");
  const now = Date.now();
  if (now - (recent.get(key) || 0) < CLIENT_REPEAT_MS) return;
  recent.set(key, now);
  const body = {
    event_id: uid(),
    event_type: eventType,
    anon_session: anonSession(),
    industry: isIndustry(industry) ? industry : null,
    service,
    cta_location: ctaLocation,
    path: window.location.pathname.slice(0, 200),
    ...utms(),
  };
  fetch(`${API}/analytics/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    keepalive: true,
  }).catch(() => {});
}

// Calendly link carrying campaign UTMs + industry (utm_term) so bookings show attribution.
export function calendlyUrl(industry = lastIndustry()) {
  if (optedOut()) return CALENDLY_URL;
  const params = new URLSearchParams(utms());
  if (industry) params.set("utm_term", industry);
  const qs = params.toString();
  return qs ? `${CALENDLY_URL}?${qs}` : CALENDLY_URL;
}

function industryFromPath() {
  const m = window.location.pathname.match(/^\/industries\/([^/]+)/);
  return m && isIndustry(m[1]) ? m[1] : null;
}

function onCalendlyClick(e) {
  const el = e.target.closest?.('a[href*="calendly.com"], [data-calendly]');
  if (!el) return;
  const testid = el.getAttribute("data-testid") || "";
  const pricing = testid.match(/^pricing-buy-(.+)$/);
  const industry = industryFromPath() || lastIndustry();
  track("calendly_cta_click", {
    industry,
    service: pricing ? pricing[1] : null,
    ctaLocation: pricing ? "pricing_card" : CTA_LOCATIONS[testid] || testid || "unknown",
  });
  if (el.tagName === "A") el.href = calendlyUrl(industry);
}

// Capture phase runs before navigation, so href rewrites apply to this click.
export function installCalendlyTracking() {
  document.addEventListener("click", onCalendlyClick, true);
  document.addEventListener("auxclick", onCalendlyClick, true);
  return () => {
    document.removeEventListener("click", onCalendlyClick, true);
    document.removeEventListener("auxclick", onCalendlyClick, true);
  };
}
